import CloseRounded from '@mui/icons-material/CloseRounded';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { forwardRef, useImperativeHandle, useRef } from 'react';

import { UploadRef } from '@components/Capture/types';
import { PieceType } from '@rdTypes/apiContract';

import Upload from './components/Upload';
import { sxAlert, sxMain } from './styles';
import { useImageUpload } from './useImageUpload';

type UploadImagesProps = {
  forceUpdate: (data: object) => void;
  onUpload: (data: Array<File | string>) => void;
  type: PieceType;
  setInvalid: (invalid: boolean) => void;
};

const UploadImages = forwardRef<unknown, unknown>((props, ref) => {
  const { forceUpdate, onUpload, type, setInvalid } = props as UploadImagesProps;
  const isUploadingRef = useRef(false);

  const { files, error, isLoading, subscriptionSummary, handleDropMultiFile, handleRemoveFile, removeAllFiles } =
    useImageUpload(type, setInvalid);

  useImperativeHandle(
    ref,
    (): UploadRef => {
      forceUpdate({});
      return files.length
        ? {
            upload: () => {
              if (isUploadingRef.current) return;
              isUploadingRef.current = true;
              onUpload(files);
            },
            removeAll: removeAllFiles,
          }
        : null;
    },
    [files, forceUpdate, onUpload, removeAllFiles],
  );

  if (isLoading || !subscriptionSummary) {
    return (
      <Box sx={sxMain} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={sxMain}>
      {!window.OffscreenCanvas && (
        <Alert severity="info" sx={sxAlert}>
          For best performance, please upload no more than 50 images at a time
        </Alert>
      )}
      {files.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1, maxWidth: 1200, mx: 'auto' }}>
          <Button variant="outlined" color="inherit" size="small" startIcon={<CloseRounded />} onClick={removeAllFiles}>
            Remove All
          </Button>
        </Box>
      )}
      <Upload error={error} files={files as File[]} onDrop={handleDropMultiFile} onRemove={handleRemoveFile} />
    </Box>
  );
});

export default UploadImages;
